import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, X, ChevronRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadMenuJpeg } from './exportMenu';
import { MenuCanvas } from './MenuCanvas';
import { normalizePages, firstPageElements } from './editorState';
import { useFonts } from './fonts';
import { Card, EmptyState } from '../layout/Primitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const useViewportWidth = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
};

export const DownloadMenuButton = ({ menu, customFonts, className = '', iconOnly = true, testId = 'download-menu-btn' }) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const result = await downloadMenuJpeg(menu, customFonts);
      if (result === 'downloaded') toast.success('JPEG saved');
    } catch (err) {
      toast.error('Could not create image');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid={testId}
      aria-label="Download as JPEG"
      title="Download as JPEG"
      className={`tap-none flex items-center justify-center gap-1.5 rounded-full text-white disabled:opacity-60 ${iconOnly ? 'h-9 w-9 bg-white/[0.08]' : 'h-10 px-4 bg-[#EAB308] text-black font-semibold'} ${className}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {!iconOnly && <span className="text-sm">{busy ? 'Preparing…' : 'Download JPEG'}</span>}
    </button>
  );
};

export const MenuViewer = ({ menu, customFonts, onClose }) => {
  const vw = useViewportWidth();
  const width = Math.min(vw, 520);
  const pages = normalizePages(menu);
  const [idx, setIdx] = useState(0);
  const scrollerRef = useRef(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  const onScroll = () => {
    const el = scrollerRef.current;
    if (el && el.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };
  const goTo = (i) => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };
  const currentMenu = { ...menu, elements: pages[idx]?.elements || [] };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex flex-col bg-black"
      data-testid="menu-viewer"
    >
      <div className="pt-safe flex items-center justify-between border-b border-white/[0.06] bg-black/70 px-4 py-3 backdrop-blur-xl">
        <h2 className="min-w-0 flex-1 truncate font-heading text-base font-semibold text-white" data-testid="menu-viewer-title">{menu.title}</h2>
        {pages.length > 1 && <span className="mr-3 shrink-0 text-xs font-semibold tabular-nums text-[#a1a1aa]" data-testid="viewer-page-indicator">{idx + 1} / {pages.length}</span>}
        <DownloadMenuButton menu={currentMenu} customFonts={customFonts} className="mr-2" />
        <button
          onClick={onClose}
          data-testid="menu-viewer-close-btn"
          aria-label="Close"
          className="tap-none flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.08] text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: 'none' }}
        data-testid="menu-viewer-scroller"
      >
        {pages.map((p) => (
          <div key={p.id} className="pb-safe h-full w-full shrink-0 snap-center snap-always overflow-y-auto">
            <div className="mx-auto" style={{ width }}>
              <MenuCanvas menu={menu} elements={p.elements} width={width} customFonts={customFonts} />
            </div>
          </div>
        ))}
      </div>
      {pages.length > 1 && (
        <div className="pb-safe pointer-events-none absolute inset-x-0 bottom-3 flex items-center justify-center gap-1.5" data-testid="menu-viewer-dots">
          {pages.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => goTo(i)}
              data-testid={`viewer-dot-${i}`}
              aria-label={`Go to page ${i + 1}`}
              className={`pointer-events-auto h-2 rounded-full transition-all ${i === idx ? 'w-6 bg-[#EAB308]' : 'w-2 bg-white/40'}`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

export const UserMenusTab = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const { customFonts } = useFonts();

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/user/menus`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMenus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 text-center text-sm text-[#71717a]">Loading…</div>;
  if (menus.length === 0) {
    return <EmptyState icon={BookOpen} title="Nothing here yet" message="Menus and offers will appear here once they're published." testId="menus-empty" />;
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="user-menus-list">
        {menus.map((m, i) => (
          <motion.button
            key={m.id}
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            onClick={() => setOpen(m)}
            data-testid={`user-menu-card-${m.id}`}
            className="tap-none group text-left active:scale-[0.99]"
          >
            <Card className="overflow-hidden transition-colors group-hover:border-[#EAB308]/50">
              <div className="relative h-56 overflow-hidden bg-black">
                <MenuCanvas menu={m} width={320} elements={firstPageElements(m)} customFonts={customFonts} className="mx-auto" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#18181b] to-transparent" />
                {(m.pages?.length || 1) > 1 && (
                  <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2 py-1 text-[10px] font-bold text-white backdrop-blur" data-testid={`menu-pages-${m.id}`}>{m.pages.length} pages</span>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-heading text-[15px] font-semibold text-white">{m.title}</p>
                  <p className="text-xs text-[#71717a]">Tap to view</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#52525b]" />
              </div>
            </Card>
          </motion.button>
        ))}
      </div>
      <AnimatePresence>{open && <MenuViewer menu={open} customFonts={customFonts} onClose={() => setOpen(null)} />}</AnimatePresence>
    </>
  );
};
